import React from "react";

const chip = (on) => `text-[9px] w-6 text-center py-0.5 rounded-full border ${on ? "bg-amber-500/80 border-amber-400 text-white" : "border-amber-400/30 text-amber-300/60"}`;

/** Apply Overhead / Apply Withholding of one procedure (filled = applies); the reason is in the tooltip. */
export function ProcBilling({ p, id }) {
    if (p.apply_overhead === undefined) return <span className="text-white/20">—</span>;
    return (
        <span className="inline-flex gap-1" data-testid={`sb-proc-billing-${id}`}>
            <span data-testid={`sb-proc-overhead-${id}`} data-on={p.apply_overhead ? "true" : "false"} className={chip(p.apply_overhead)}
                  title={`${p.overhead_reason || ""}${p.overhead_quote ? ` — “${p.overhead_quote}”` : ""}`}>OH</span>
            <span data-testid={`sb-proc-withholding-${id}`} data-on={p.apply_withholding ? "true" : "false"} className={chip(p.apply_withholding)}
                  title={`${p.withholding_reason || ""}${p.withholding_quote ? ` — “${p.withholding_quote}”` : ""}`}>WH</span>
        </span>
    );
}

/** Quote under the label when a procedure carries no overhead per the budget / contract. */
export function ProcBillingNote({ p, id }) {
    if (p.apply_overhead !== false || !p.overhead_quote) return null;
    return (
        <div className="text-[10px] text-white/55" data-testid={`sb-proc-overhead-note-${id}`}>
            <span className="text-amber-300">OH off</span><span className="text-white/45"> · {p.overhead_reason}</span>
            <span className="italic text-white/70"> — “{p.overhead_quote}”</span>
        </div>
    );
}

/** The visit's overhead row: the procedures carry it, so the budget's own overhead line is not listed again. */
export function OverheadRow({ v, money, i }) {
    const oh = v.overhead;
    if (!oh || !oh.percent || !oh.on_usd) return null;
    return (
        <tr className="border-t border-white/5 text-white/50" data-testid={`sb-visit-overhead-${i}`}>
            <td className="px-3 py-1.5" colSpan={2}>Overhead {oh.percent}% on {money(oh.on_usd)} <span className="text-white/30">· procedures marked OH</span>
                {oh.budget_line_usd != null && Math.abs(oh.budget_line_usd - oh.amount_usd) > 0.05 &&
                    <span className="text-amber-300"> · budget line {money(oh.budget_line_usd)}</span>}</td>
            <td className="px-3 py-1.5">computed</td><td /><td /><td /><td className="px-3 py-1.5 text-right tabular-nums">{money(oh.amount_usd)}</td>
        </tr>
    );
}

/** Additional (invoiceable) procedures attached to a visit — each with its OH · WH flags. */
export function AdditionalProcedureRows({ v, money, i }) {
    return (v.additional_procedures || []).map((ap, j) => (
        <tr key={`add-${j}`} className="border-t border-white/5 text-white/60" data-testid={`sb-visit-additional-${i}-${j}`}>
            <td className="px-3 py-1.5" colSpan={2}>{ap.procedure || ap.label}{ap.code ? <span className="text-white/30"> · {ap.code}</span> : null} <span className="text-sky-300/80">· additional</span></td>
            <td className="px-3 py-1.5">budget</td><td /><td />
            <td className="px-3 py-1.5"><ProcBilling p={ap} id={`add-${i}-${j}`} /></td>
            <td className="px-3 py-1.5 text-right tabular-nums">{money(ap.unit_cost ?? ap.total_usd)}</td>
        </tr>
    ));
}

/** Study-level additional procedures (not tied to one visit) with their OH · WH flags. */
export function StudyAdditionalProcedures({ items, money }) {
    if (!items?.length) return null;
    return (
        <div className="border border-white/10 rounded-lg bg-white/[0.02]" data-testid="sb-study-additional">
            <div className="p-3 text-sm font-medium flex items-center gap-2">Additional procedures <span className="text-white/40 text-xs font-normal">· study level · invoiced when performed</span></div>
            <table className="w-full text-xs border-t border-white/5">
                <thead className="text-white/40"><tr><th className="text-left px-3 py-1.5 font-normal">Procedure</th><th className="text-left px-3 py-1.5 font-normal">Code</th><th className="text-left px-3 py-1.5 font-normal">Invoiceable</th><th className="text-left px-3 py-1.5 font-normal" title="Apply Overhead · Apply Withholding">OH · WH</th><th className="text-right px-3 py-1.5 font-normal">Unit cost</th></tr></thead>
                <tbody>
                    {items.map((ap, j) => (
                        <tr key={`${ap.code}-${j}`} className="border-t border-white/5" data-testid={`sb-study-additional-${j}`}>
                            <td className="px-3 py-1.5 text-white/80">{ap.procedure || ap.label}<ProcBillingNote p={ap} id={`study-${j}`} /></td>
                            <td className="px-3 py-1.5 font-mono text-white/50">{ap.code || "—"}</td>
                            <td className="px-3 py-1.5 text-white/60">{ap.invoiceable === false ? "no" : "yes"}</td>
                            <td className="px-3 py-1.5"><ProcBilling p={ap} id={`study-${j}`} /></td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{money(ap.unit_cost ?? ap.total_usd)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
